// TODO: COO-215 complete types and unit test
// @ts-nocheck
/* eslint-disable @typescript-eslint/naming-convention */
import isDate from 'lodash/isDate';
import isPlainObject from 'lodash/isPlainObject';
import reduce from 'lodash/reduce';
import size from 'lodash/size';

let scheduleTimer: number | boolean = false;
const eventQueue: {
  eventName: string;
  payload: object;
}[] = [];

/**
 * Return true if all the trackers are ready to accept events.
 */
function areTrackersReady() {
  return !!window.clevertap;
}

function isClevertapSupportedType(value) {
  const supportedTypes = ['string', 'number', 'boolean'];

  if (supportedTypes.includes(typeof value) || isDate(value)) {
    return true;
  }

  return false;
}

function divideLongStringIntoMultipleKeys(str, key) {
  const result = {};
  const chunks: string[] = [];

  for (let i = 0; i < str.length; i += 512) {
    chunks.push(str.substr(i, 512));
  }

  // First chunk is normal
  result[key] = chunks.shift();

  // Subsequent chunks need suffix
  chunks.forEach((chunk, index) => {
    result[`${key}_${index + 1}`] = chunk;
  });

  return result;
}

/**
 * Cancel existing schedule to avoid unexpected events.
 */
// TODO: COO-215
/* istanbul ignore next */
function cancelSchedule() {
  // Clear existing timer if exists
  if (scheduleTimer) {
    clearTimeout(scheduleTimer as number);
    scheduleTimer = false;
  }
}

/**
 * We need to normalise our event payload according to the limitation listed at
 * https://developer.clevertap.com/docs/concepts-events#section-platform-considerations.
 *
 * @param {*} payload Things that we want to send to Clevertap
 */
function normaliseClevertapPayload(payload: object | undefined) {
  if (isPlainObject(payload)) {
    return reduce(
      payload,
      (memo, value, key) => {
        if (value === undefined) {
          return memo;
        }

        let normalised = {
          [key]: value,
        };

        if (typeof value === 'string') {
          let sanitizedString = value;

          const mapping = {
            '&': '_amp_',
            $: '_dol_',
            '\\': '_bslash_',
            '%': '_pct_',
            '>': '_gt_',
            '<': '_lt_',
            '!': '_bang_',
          };

          for (const [k, v] of Object.entries(mapping)) {
            sanitizedString = sanitizedString.split(k).join(v);
          }

          normalised = divideLongStringIntoMultipleKeys(sanitizedString, key);
        } else if (isPlainObject(value) || Array.isArray(value)) {
          const jsonString = JSON.stringify(value);

          normalised = normaliseClevertapPayload({
            [key]: jsonString,
          });
        } else if (!isClevertapSupportedType(value)) {
          throw new TypeError(`Type '${typeof value}' for key '${key}' is not supported.`);
        }

        Object.assign(memo, normalised);

        return memo;
      },
      {},
    );
  }

  return normaliseClevertapPayload({
    property: payload,
  });
}

/**
 * Actually send events to any trackers we need.
 */
function sendEventToTrackers(eventName: string, payload: object | undefined = undefined) {
  // Clevertap events require special treatment
  if (!payload) {
    window?.clevertap?.event.push(eventName);
  } else {
    // Normalise our payload first
    const normalisedPayload = normaliseClevertapPayload(payload);

    // If there are more keys after normalisation, chunking happened -> BAD
    const isPotentiallyLargeEvent = size(normalisedPayload) > size(payload);

    // A preventive event to find out what might go wrong
    if (isPotentiallyLargeEvent) {
      sendEventToTrackers('Potential Large Event', {
        'Original Event Name': eventName,
      });
    }

    // Send the normalised payload
    window?.clevertap?.event.push(eventName, normalisedPayload);

    // An indicator on what's wrong
    if (size(normalisedPayload) > 256) {
      sendEventToTrackers('Event Too Large', {
        'Original Event Name': eventName,
      });
    }
  }
}

/**
 * Send all the events in our queue then clears it.
 */
// TODO: COO-215
/* istanbul ignore next */
function sendScheduledEvents() {
  let promise = Promise.resolve();

  // Delay 10ms for each event to give a buffer for correct order
  eventQueue.forEach(({ eventName, payload }) => {
    promise = promise.then(() => {
      return new Promise((resolve) => {
        setTimeout(() => {
          sendEventToTrackers(eventName, payload);
          resolve();
        }, 10);
      });
    });
  });

  eventQueue.length = 0;

  return promise;
}

/**
 * Schedule the next event push so that buffered events will eventually be sent.
 */
// TODO: COO-215
/* istanbul ignore next */
function scheduleNextSend() {
  cancelSchedule();

  // Schedule new timer
  scheduleTimer = setTimeout(() => {
    if (areTrackersReady()) {
      sendScheduledEvents();
    } else {
      scheduleNextSend();
    }
  }, 500);
}

/**
 * Put a new event into queue and schedule the next event push.
 *
 * @param {string} eventName The name of tracked event.
 * @param {*} payload Event properties, can be anything but preferably object.
 */
// TODO: COO-215
/* istanbul ignore next */
function scheduleEventForLater(eventName: string, payload: object | undefined = undefined) {
  eventQueue.push({
    eventName,
    payload,
  });
  scheduleNextSend();
}

export { sendEventToTrackers, normaliseClevertapPayload };

/**
 * The entry point to sending events to trackers.
 * Any special handlings will be done here.
 *
 * Handlings:
 * - Check if all trackers are ready, if not, buffer the events and send later
 *
 * @param {string} eventName The name of tracked event.
 * @param {*} payload Event properties, can be anything but preferably object.
 */
export default function pushClevertapEvent(eventName: string, payload: object | undefined) {
  const isReady = areTrackersReady();

  // TODO: COO-215
  /* istanbul ignore else */
  if (isReady) {
    // Clear buffered events first to have correct order
    cancelSchedule();
    sendScheduledEvents().then(() => {
      sendEventToTrackers(eventName, payload);
    });
  } else {
    scheduleEventForLater(eventName, payload);
  }
}
